<script setup lang="ts">
/**
 * Day columns over an hour axis — the day, week and n-day views of a calendar,
 * a room plan, a shift board. One column per ISO date in `dates`; entries are
 * blocks sized by their duration, overlapping ones share the width. All-day
 * and day-long entries (and timed ones outside the visible hours) sit in the
 * lane under the header.
 *
 * Empty time is interactive: a click reports the slot, a double-click or Enter
 * on a focused slot asks to `create` an entry there. Arrow keys walk the slots
 * (roving tabindex), so the grid is one Tab stop plus its entries.
 *
 * Drag is HTML drag and drop and opt-in: `draggableEvents` moves blocks,
 * `resizableEvents` adds an end handle. A drop reports the slot and — when the
 * drag began here — the entry and a proposed new start/end. `dropActive`
 * accepts drops of drags that began elsewhere.
 *
 * Look: `hourHeight` sets the height of an hour (default 3 × the size unit),
 * `showIsoWeek` puts „KW 41“ into the corner (or the `corner` slot does),
 * `maxAllDayRows` caps the all-day band with „+N weitere“ per day (a click
 * opens the whole band), `dimPast` fades entries that are over, `dayCaption`
 * adds a short line under each day's number (say, its number of entries).
 *
 * `look="desktop"` is the native calendar's chrome: the weekday in small
 * capitals over a large day number, a wider hour gutter, the clock on the now
 * line, hour lines with finer half hours (no dotted slot rules) and
 * `NxEventChip`'s desktop look. The default `classic` keeps the one-line head
 * and dotted slots every other consumer has.
 */
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { isoWeekNumber } from '../Calendar/calendarGrid';
import { dateTimeFormat } from '../Format/intlCache';
import { formatTime } from '../Format/date';
import { i18nInjectionKey } from '../internal';
import NxEventChip, { type NxEventChipStatusLabels } from './NxEventChip.vue';
import { addLocalDays, eventsOnDay, fromIsoDate, isEventPast, toIsoDate } from './eventDays';
import { capLanes } from './monthLayout';
import {
  layoutAllDayLane,
  layoutDayColumn,
  MIN_EVENT_MINUTES,
  slotBounds,
  wallMinutes,
  type NxTimeGridLaneItem,
  type NxTimeGridSegment,
} from './timeGridLayout';
import type {
  NxCalendarDrop,
  NxCalendarEvent,
  NxCalendarEventKey,
  NxCalendarSlot,
  NxEventDragMode,
  NxEventDragStart,
} from './types';
import { useNow } from './useNow';

import type { NxCalendarEventAppearance } from './types';

/** One column's day, as the `day-header` slot and `dayCaption` get it. */
export interface NxTimeGridDay {
  /** ISO `YYYY-MM-DD`. */
  date: string;
  day: Date;
  /** The entries that touch the day. */
  events: NxCalendarEvent[];
  isToday: boolean;
}

const props = withDefaults(
  defineProps<{
    /** ISO `YYYY-MM-DD` per column, in order: one for a day view, seven for a week. */
    dates: string[];
    events: NxCalendarEvent[];
    eventAppearance?: NxCalendarEventAppearance;
    /** First visible hour, 0-23. */
    startHour?: number;
    /** End of the visible hours, exclusive, 1-24. */
    endHour?: number;
    /** Granularity of slots, clicks and drops in minutes. */
    slotMinutes?: number;
    /** Always show the all-day lane; off, it only appears when it has entries. */
    allDayLane?: boolean;
    /** Rows of the all-day lane before „+N weitere“ takes the last one. Unset: all rows. */
    maxAllDayRows?: number;
    /** Height of one hour, any CSS length. Unset: three size units. */
    hourHeight?: string;
    /** „KW 41“ in the corner over the hour gutter: the ISO week of the first column. */
    showIsoWeek?: boolean;
    weekPrefix?: string;
    /** Fade entries whose end has passed (or that say `past`). */
    dimPast?: boolean;
    /** A short line under the day number of each column, e.g. „3 Termine“; empty: none. */
    dayCaption?: (day: NxTimeGridDay) => string;
    /** `classic`: one-line head, dotted slots. `desktop`: the native calendar's chrome and chips. */
    look?: 'classic' | 'desktop';
    nowIndicator?: boolean;
    draggableEvents?: boolean;
    resizableEvents?: boolean;
    /** A drag that began outside this grid is in progress: accept its drop. */
    dropActive?: boolean;
    selectedEventId?: NxCalendarEventKey | null;
    locale?: string;
    ariaLabel?: string;
    allDayLabel?: string;
    /** `{count}` is replaced by the number of hidden all-day entries of the day. */
    moreLabel?: string;
    /** Folds an opened all-day lane back to `maxAllDayRows`. */
    lessLabel?: string;
    untitledLabel?: string;
    resizeLabel?: string;
    statusLabels?: Partial<NxEventChipStatusLabels>;
  }>(),
  {
    startHour: 0,
    endHour: 24,
    slotMinutes: 30,
    allDayLane: true,
    maxAllDayRows: undefined,
    hourHeight: undefined,
    showIsoWeek: false,
    weekPrefix: 'KW',
    dimPast: false,
    dayCaption: undefined,
    look: 'classic',
    nowIndicator: true,
    draggableEvents: false,
    resizableEvents: false,
    dropActive: false,
    selectedEventId: null,
    locale: undefined,
    ariaLabel: 'Terminraster',
    allDayLabel: 'Ganztägig',
    moreLabel: '+{count} weitere',
    lessLabel: 'Weniger',
    untitledLabel: '(Ohne Titel)',
    resizeLabel: 'Dauer ändern',
    statusLabels: () => ({}),
  }
);

const emit = defineEmits<{
  slotClick: [slot: NxCalendarSlot, event: MouseEvent];
  /** Double-click or Enter on an empty slot: the consumer creates an entry. */
  create: [slot: NxCalendarSlot];
  slotContextmenu: [slot: NxCalendarSlot, event: MouseEvent];
  eventClick: [event: NxCalendarEvent, nativeEvent: MouseEvent];
  eventDblclick: [event: NxCalendarEvent, nativeEvent: MouseEvent];
  eventContextmenu: [event: NxCalendarEvent, nativeEvent: MouseEvent];
  eventDragStart: [payload: NxEventDragStart];
  eventDragEnd: [];
  drop: [payload: NxCalendarDrop];
}>();

defineSlots<{
  /** Replaces the default `NxEventChip` of a block or lane bar. */
  event?: (props: { event: NxCalendarEvent; allDay: boolean }) => unknown;
  /** The corner over the hour gutter; `isoWeek` is the ISO week of the first column. */
  corner?: (props: { isoWeek: number | null }) => unknown;
  /** Replaces the column header's weekday, day number and caption. */
  'day-header'?: (props: NxTimeGridDay) => unknown;
}>();

const i18n = inject(i18nInjectionKey, null);
const resolvedLocale = computed(() => props.locale ?? i18n?.locale?.value ?? 'de-DE');
const now = useNow();

const hours = computed(() => {
  const start = Math.min(23, Math.max(0, Math.floor(props.startHour)));
  const end = Math.min(24, Math.max(start + 1, Math.ceil(props.endHour)));
  return { start, end, count: end - start };
});

/** A slot size that tiles the hour; anything else would drift off the hour lines. */
const slotSize = computed(() => {
  const wanted = Math.max(5, Math.round(props.slotMinutes));
  return 60 % wanted === 0 || wanted % 60 === 0 ? wanted : 30;
});

const slotsPerColumn = computed(() => Math.ceil((hours.value.count * 60) / slotSize.value));
const rangeMinutes = computed(() => hours.value.count * 60);

const todayIso = computed(() => toIsoDate(now.value));

// A short entry is drawn at least as tall as the CSS `min-height` of a block;
// the overlap maths must reserve that height, not a fixed 15 minutes, or the
// next entry lands on top of it. Measured from the first hour and block.
const minMinutes = ref(MIN_EVENT_MINUTES);

function measureMinMinutes(): void {
  const hour = root.value?.querySelector<HTMLElement>('.nx-time-grid__hour')?.offsetHeight ?? 0;
  const block = root.value?.querySelector<HTMLElement>('.nx-time-grid__event');
  const minHeight = block ? Number.parseFloat(getComputedStyle(block).minHeight) : 0;
  if (hour <= 0 || !Number.isFinite(minHeight) || minHeight <= 0) return;
  const minutes = Math.max(MIN_EVENT_MINUTES, Math.ceil((minHeight / hour) * 60));
  if (minutes !== minMinutes.value) minMinutes.value = minutes;
}

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  void nextTick(measureMinMinutes);
  if (typeof ResizeObserver === 'undefined' || !root.value) return;
  resizeObserver = new ResizeObserver(() => measureMinMinutes());
  resizeObserver.observe(root.value);
});

onBeforeUnmount(() => resizeObserver?.disconnect());

watch(
  () => [props.hourHeight, props.startHour, props.endHour, props.events],
  () => void nextTick(measureMinMinutes)
);

const columns = computed(() =>
  props.dates.map((iso) => {
    const day = fromIsoDate(iso) ?? new Date(NaN);
    const layout = layoutDayColumn(
      props.events,
      iso,
      hours.value.start,
      hours.value.end,
      minMinutes.value
    );
    const dayEvents = Number.isNaN(day.getTime()) ? [] : eventsOnDay(props.events, day);
    const isToday = iso === todayIso.value;
    const caption = props.dayCaption?.({ date: iso, day, events: dayEvents, isToday }) ?? '';
    return { iso, day, layout, dayEvents, isToday, caption };
  })
);

const lane = computed(() =>
  layoutAllDayLane(
    props.events,
    props.dates,
    columns.value.map((column) => column.layout.outside)
  )
);
const showLane = computed(() => props.allDayLane || lane.value.items.length > 0);

/** The all-day lane opened past `maxAllDayRows` by a click on „+N weitere“. */
const laneOpen = ref(false);
watch(
  () => props.dates.join('|'),
  () => {
    laneOpen.value = false;
  }
);

const laneView = computed(() => {
  const { items, rows } = lane.value;
  const max = props.maxAllDayRows;
  const capped = max !== undefined && max >= 1 && rows > Math.floor(max);
  if (!capped || laneOpen.value) {
    return { items, rows, more: [] as Array<{ column: number; count: number }>, canFold: capped };
  }
  const limit = Math.floor(max);
  const { shown, hidden } = capLanes(items, props.dates.length, limit);
  return {
    items: shown,
    rows: limit,
    more: hidden.map((count, column) => ({ column, count })).filter((entry) => entry.count > 0),
    canFold: false,
  };
});

function moreText(count: number): string {
  return props.moreLabel.replace('{count}', String(count));
}

// Opening and folding the band removes the button that was pressed; the
// focus goes on to its counterpart instead of falling back to the page.
const laneToggle = ref<HTMLButtonElement | null>(null);
/** The column whose „+N weitere“ opened the band: folding returns there. */
let openedFromColumn = 0;

/** Opened: the focus lands on „Weniger“, the revealed entries follow it in Tab order. */
async function openLane(column: number): Promise<void> {
  openedFromColumn = column;
  laneOpen.value = true;
  await nextTick();
  laneToggle.value?.focus();
}

/** Folded: the focus goes back to the „+N weitere“ it came from (or the first one left). */
async function foldLane(): Promise<void> {
  laneOpen.value = false;
  await nextTick();
  const more =
    root.value?.querySelector<HTMLElement>(`[data-lane-more="${openedFromColumn}"]`) ??
    root.value?.querySelector<HTMLElement>('.nx-time-grid__lane-more');
  more?.focus();
}

const isoWeek = computed(() => {
  const first = props.dates.map((iso) => fromIsoDate(iso)).find(Boolean);
  return first ? isoWeekNumber(first.getFullYear(), first.getMonth() + 1, first.getDate()) : null;
});

function isPast(event: NxCalendarEvent): boolean {
  return props.dimPast && isEventPast(event, now.value);
}

const gridStyle = computed(() => ({
  '--tg-columns': String(Math.max(1, props.dates.length)),
  '--tg-hours': String(hours.value.count),
  ...(props.hourHeight ? { '--tg-hour-height': props.hourHeight } : {}),
}));

const desktop = computed(() => props.look === 'desktop');

const headerFormat = computed(() => dateTimeFormat(resolvedLocale.value, { weekday: 'short' }));

/** The desktop head is a label over the day number: „Mo“, not „Mo.“. */
function weekdayLabel(day: Date): string {
  if (Number.isNaN(day.getTime())) return '';
  const label = headerFormat.value.format(day);
  return desktop.value ? label.replace(/\.$/, '') : label;
}
const fullFormat = computed(() =>
  dateTimeFormat(resolvedLocale.value, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
);

function hourLabel(hour: number): string {
  return formatTime(new Date(2000, 0, 1, hour), { locale: resolvedLocale.value });
}

const hourLabels = computed(() =>
  Array.from({ length: hours.value.count }, (_, index) => {
    const hour = hours.value.start + index;
    return { hour, label: hourLabel(hour) };
  })
);

function slotAt(dayIndex: number, slotIndex: number): NxCalendarSlot | null {
  const iso = props.dates[dayIndex];
  if (!iso) return null;
  const bounds = slotBounds(
    iso,
    hours.value.start * 60 + slotIndex * slotSize.value,
    slotSize.value
  );
  return bounds ? { date: iso, ...bounds, allDay: false } : null;
}

function laneSlot(dayIndex: number): NxCalendarSlot | null {
  const iso = props.dates[dayIndex];
  const day = iso ? fromIsoDate(iso) : null;
  if (!iso || !day) return null;
  return { date: iso, start: day, end: addLocalDays(day, 1), allDay: true };
}

function dayLabel(day: Date, iso: string): string {
  return Number.isNaN(day.getTime()) ? iso : fullFormat.value.format(day);
}

function slotLabel(slot: NxCalendarSlot | null): string {
  if (!slot) return '';
  return `${fullFormat.value.format(slot.start)}, ${formatTime(slot.start, {
    locale: resolvedLocale.value,
  })}`;
}

/**
 * Below this length a block is too low for the stacked title and time: it gets
 * the one-line chip („09:00 Daily Standup“) instead of a clipped second line.
 */
const SHORT_BLOCK_MINUTES = 45;

function chipSize(segment: NxTimeGridSegment): 'compact' | 'regular' {
  return segment.endMinute - segment.startMinute < SHORT_BLOCK_MINUTES ? 'compact' : 'regular';
}

function segmentStyle(segment: NxTimeGridSegment): Record<string, string> {
  const total = rangeMinutes.value;
  const top = ((segment.startMinute - hours.value.start * 60) / total) * 100;
  const height = ((segment.endMinute - segment.startMinute) / total) * 100;
  return {
    top: `${top}%`,
    height: `${height}%`,
    left: `${(segment.track / segment.tracks) * 100}%`,
    width: `${(segment.span / segment.tracks) * 100}%`,
  };
}

function laneStyle(item: NxTimeGridLaneItem): Record<string, string> {
  return {
    gridColumn: `${item.column + 2} / span ${item.span}`,
    gridRow: String(item.row + 1),
  };
}

const laneRows = computed(() => Math.max(1, laneView.value.rows));

/** Hour lines solid, half hours a finer line, finer slots none. */
function slotLine(slotIndex: number): string {
  const minute = hours.value.start * 60 + slotIndex * slotSize.value;
  if (minute % 60 === 0) return 'is-hour';
  if (minute % 30 === 0) return 'is-half';
  return '';
}

const nowLine = computed(() => {
  if (!props.nowIndicator) return null;
  const index = columns.value.findIndex((column) => column.isToday);
  if (index === -1) return null;
  const minute = wallMinutes(now.value) - hours.value.start * 60;
  if (minute < 0 || minute > rangeMinutes.value) return null;
  return {
    index,
    top: `${(minute / rangeMinutes.value) * 100}%`,
    // The clock in the gutter belongs to the desktop look.
    label: desktop.value ? formatTime(now.value, { locale: resolvedLocale.value }) : '',
  };
});

// --- keyboard: one roving tab stop over the slots -------------------------

const active = ref({ day: 0, slot: 0 });

function resetActive(): void {
  const todayIndex = columns.value.findIndex((column) => column.isToday);
  const hour = now.value.getHours();
  const slot =
    todayIndex !== -1 && hour >= hours.value.start && hour < hours.value.end
      ? Math.floor(((hour - hours.value.start) * 60) / slotSize.value)
      : 0;
  active.value = { day: Math.max(0, todayIndex), slot };
}

watch(
  () => [props.dates.join('|'), hours.value.start, hours.value.end, slotSize.value],
  resetActive,
  { immediate: true }
);

const root = ref<HTMLElement | null>(null);

async function moveActive(day: number, slot: number): Promise<void> {
  active.value = {
    day: Math.min(Math.max(0, day), Math.max(0, props.dates.length - 1)),
    slot: Math.min(Math.max(0, slot), slotsPerColumn.value - 1),
  };
  await nextTick();
  root.value
    ?.querySelector<HTMLElement>(`[data-slot="${active.value.day}-${active.value.slot}"]`)
    ?.focus();
}

function onSlotKeydown(event: KeyboardEvent, dayIndex: number, slotIndex: number): void {
  if (event.ctrlKey || event.altKey || event.metaKey) return;
  const moves: Record<string, [number, number]> = {
    ArrowUp: [dayIndex, slotIndex - 1],
    ArrowDown: [dayIndex, slotIndex + 1],
    ArrowLeft: [dayIndex - 1, slotIndex],
    ArrowRight: [dayIndex + 1, slotIndex],
    Home: [dayIndex, 0],
    End: [dayIndex, slotsPerColumn.value - 1],
  };
  const target = moves[event.key];
  if (target) {
    event.preventDefault();
    void moveActive(target[0], target[1]);
    return;
  }
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    const slot = slotAt(dayIndex, slotIndex);
    if (slot) emit('create', slot);
  }
}

function onSlotClick(event: MouseEvent, dayIndex: number, slotIndex: number): void {
  active.value = { day: dayIndex, slot: slotIndex };
  const slot = slotAt(dayIndex, slotIndex);
  if (slot) emit('slotClick', slot, event);
}

function onSlotDblclick(dayIndex: number, slotIndex: number): void {
  const slot = slotAt(dayIndex, slotIndex);
  if (slot) emit('create', slot);
}

function onSlotContextmenu(event: MouseEvent, slot: NxCalendarSlot | null): void {
  if (slot) emit('slotContextmenu', slot, event);
}

function onLaneDblclick(dayIndex: number): void {
  const slot = laneSlot(dayIndex);
  if (slot) emit('create', slot);
}

function onLaneClick(event: MouseEvent, dayIndex: number): void {
  const slot = laneSlot(dayIndex);
  if (slot) emit('slotClick', slot, event);
}

// --- drag and drop ---------------------------------------------------------

const dragOperation = ref<NxEventDragStart | null>(null);
/** Set a tick after dragstart: changing the DOM inside dragstart cancels the drag. */
const dragging = ref(false);
const dropKey = ref<string | null>(null);

function onEventDragStart(nativeEvent: DragEvent, event: NxCalendarEvent): void {
  const fromHandle = Boolean(
    (nativeEvent.target as HTMLElement | null)?.closest?.('[data-resize-handle]')
  );
  const mode: NxEventDragMode = fromHandle ? 'resize' : 'move';
  const allowed =
    !event.readonly &&
    (mode === 'move' ? props.draggableEvents : props.resizableEvents && !event.allDay);
  if (!allowed) return;
  if (nativeEvent.dataTransfer) {
    nativeEvent.dataTransfer.effectAllowed = 'move';
    if (!nativeEvent.dataTransfer.types?.length) {
      nativeEvent.dataTransfer.setData('text/plain', String(event.id));
    }
  }
  dragOperation.value = { event, mode };
  emit('eventDragStart', { event, mode });
  setTimeout(() => {
    dragging.value = dragOperation.value !== null;
  }, 0);
}

function clearDrag(): void {
  dragOperation.value = null;
  dragging.value = false;
  dropKey.value = null;
}

function onEventDragEnd(): void {
  clearDrag();
  emit('eventDragEnd');
}

const acceptsDrop = computed(() => dragOperation.value !== null || props.dropActive);

function onDragOver(event: DragEvent, key: string): void {
  if (!acceptsDrop.value) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  dropKey.value = key;
}

function proposal(operation: NxEventDragStart, slot: NxCalendarSlot): { start: Date; end: Date } {
  const { event, mode } = operation;
  if (mode === 'resize') {
    const minimumEnd = event.start.getTime() + slotSize.value * 60_000;
    return { start: event.start, end: new Date(Math.max(slot.end.getTime(), minimumEnd)) };
  }
  const hasEnd = event.end instanceof Date && event.end.getTime() > event.start.getTime();
  const duration = hasEnd ? event.end!.getTime() - event.start.getTime() : 0;
  if (event.allDay && slot.allDay) {
    // Whole days, counted as calendar days: a DST change in between must not
    // turn a three-day absence into two days and 23 hours.
    const days = Math.max(1, Math.round(duration / 86_400_000));
    return { start: slot.start, end: addLocalDays(slot.start, days) };
  }
  return { start: slot.start, end: new Date(slot.start.getTime() + duration) };
}

function onDrop(event: DragEvent, slot: NxCalendarSlot | null): void {
  if (!slot || !acceptsDrop.value) return;
  event.preventDefault();
  const operation = dragOperation.value;
  const proposed = operation ? proposal(operation, slot) : null;
  emit('drop', {
    slot,
    event: operation?.event ?? null,
    mode: operation?.mode ?? null,
    start: proposed?.start ?? null,
    end: proposed?.end ?? null,
  });
  // The source may be re-rendered away by the move and never fire dragend.
  clearDrag();
}

const chipProps = computed(() => ({
  appearance: props.eventAppearance,
  look: props.look,
  draggable: props.draggableEvents,
  locale: resolvedLocale.value,
  untitledLabel: props.untitledLabel,
  allDayLabel: props.allDayLabel,
  resizeLabel: props.resizeLabel,
  statusLabels: props.statusLabels,
}));
</script>

<template>
  <div
    ref="root"
    :aria-label="ariaLabel"
    :class="['nx-time-grid', { 'is-dragging': dragging, 'is-desktop': desktop }]"
    role="group"
    :style="gridStyle"
  >
    <div class="nx-time-grid__scroll">
      <div class="nx-time-grid__head">
        <div class="nx-time-grid__row">
          <div class="nx-time-grid__corner">
            <slot :iso-week="isoWeek" name="corner">
              <span v-if="showIsoWeek && isoWeek !== null" class="nx-time-grid__week">
                {{ weekPrefix }} {{ isoWeek }}
              </span>
            </slot>
          </div>
          <div
            v-for="column in columns"
            :key="`head-${column.iso}`"
            :aria-label="dayLabel(column.day, column.iso)"
            :class="['nx-time-grid__day-head', { 'is-today': column.isToday }]"
          >
            <slot
              :date="column.iso"
              :day="column.day"
              :events="column.dayEvents"
              :is-today="column.isToday"
              name="day-header"
            >
              <span class="nx-time-grid__weekday">{{ weekdayLabel(column.day) }}</span>
              <span
                class="nx-time-grid__daynum"
                :aria-current="column.isToday ? 'date' : undefined"
              >
                {{ Number.isNaN(column.day.getTime()) ? column.iso : column.day.getDate() }}
              </span>
              <span v-if="column.caption" class="nx-time-grid__day-caption">
                {{ column.caption }}
              </span>
            </slot>
          </div>
        </div>
        <div
          v-if="showLane"
          class="nx-time-grid__row nx-time-grid__lane"
          :style="{ gridTemplateRows: `repeat(${laneRows}, auto)` }"
        >
          <div class="nx-time-grid__lane-label" :style="{ gridRow: `1 / span ${laneRows}` }">
            <span class="nx-time-grid__lane-label-text">{{ allDayLabel }}</span>
            <button
              v-if="laneView.canFold"
              ref="laneToggle"
              aria-expanded="true"
              class="nx-time-grid__lane-toggle"
              type="button"
              @click="foldLane"
            >
              {{ lessLabel }}
            </button>
          </div>
          <div
            v-for="(column, dayIndex) in columns"
            :key="`lane-${column.iso}`"
            :aria-label="`${allDayLabel}, ${dayLabel(column.day, column.iso)}`"
            :class="[
              'nx-time-grid__lane-cell',
              { 'is-drop-target': dropKey === `lane-${dayIndex}` },
            ]"
            :data-lane="dayIndex"
            :style="{ gridColumn: String(dayIndex + 2), gridRow: `1 / span ${laneRows}` }"
            @click="onLaneClick($event, dayIndex)"
            @contextmenu="onSlotContextmenu($event, laneSlot(dayIndex))"
            @dblclick="onLaneDblclick(dayIndex)"
            @dragleave="dropKey = null"
            @dragover="onDragOver($event, `lane-${dayIndex}`)"
            @drop="onDrop($event, laneSlot(dayIndex))"
          ></div>
          <div
            v-for="item in laneView.items"
            :key="`lane-item-${item.event.id}-${item.column}`"
            :class="[
              'nx-time-grid__lane-event',
              {
                'continues-before': item.continuesBefore,
                'continues-after': item.continuesAfter,
              },
            ]"
            :style="laneStyle(item)"
            @click="emit('eventClick', item.event, $event)"
            @contextmenu="emit('eventContextmenu', item.event, $event)"
            @dblclick.stop="emit('eventDblclick', item.event, $event)"
            @dragend="onEventDragEnd"
            @dragstart="onEventDragStart($event, item.event)"
          >
            <slot :all-day="true" :event="item.event" name="event">
              <NxEventChip
                v-bind="chipProps"
                :event="item.event"
                :past="isPast(item.event)"
                :selected="selectedEventId !== null && selectedEventId === item.event.id"
                :show-time="item.outsideHours"
                size="compact"
              />
            </slot>
          </div>
          <button
            v-for="entry in laneView.more"
            :key="`lane-more-${entry.column}`"
            aria-expanded="false"
            class="nx-time-grid__lane-more"
            :data-lane-more="entry.column"
            :style="{ gridColumn: String(entry.column + 2), gridRow: String(laneRows) }"
            type="button"
            @click="openLane(entry.column)"
          >
            {{ moreText(entry.count) }}
          </button>
        </div>
      </div>

      <div class="nx-time-grid__row nx-time-grid__body">
        <div class="nx-time-grid__gutter" aria-hidden="true">
          <div v-for="hour in hourLabels" :key="`hour-${hour.hour}`" class="nx-time-grid__hour">
            <span>{{ hour.label }}</span>
          </div>
          <span
            v-if="nowLine && nowLine.label"
            class="nx-time-grid__now-time"
            :style="{ top: nowLine.top }"
          >
            {{ nowLine.label }}
          </span>
        </div>
        <div
          v-for="(column, dayIndex) in columns"
          :key="`col-${column.iso}`"
          :aria-label="dayLabel(column.day, column.iso)"
          :class="['nx-time-grid__column', { 'is-today': column.isToday }]"
          role="group"
        >
          <div
            v-for="slotIndex in slotsPerColumn"
            :key="`slot-${slotIndex}`"
            :aria-label="slotLabel(slotAt(dayIndex, slotIndex - 1))"
            :class="[
              'nx-time-grid__slot',
              slotLine(slotIndex - 1),
              {
                'is-drop-target': dropKey === `${dayIndex}-${slotIndex - 1}`,
              },
            ]"
            :data-slot="`${dayIndex}-${slotIndex - 1}`"
            role="button"
            :tabindex="active.day === dayIndex && active.slot === slotIndex - 1 ? 0 : -1"
            @click="onSlotClick($event, dayIndex, slotIndex - 1)"
            @contextmenu="onSlotContextmenu($event, slotAt(dayIndex, slotIndex - 1))"
            @dblclick="onSlotDblclick(dayIndex, slotIndex - 1)"
            @dragleave="dropKey = null"
            @dragover="onDragOver($event, `${dayIndex}-${slotIndex - 1}`)"
            @drop="onDrop($event, slotAt(dayIndex, slotIndex - 1))"
            @keydown="onSlotKeydown($event, dayIndex, slotIndex - 1)"
          ></div>
          <div
            v-for="segment in column.layout.segments"
            :key="`seg-${segment.event.id}`"
            :class="[
              'nx-time-grid__event',
              {
                'continues-before': segment.continuesBefore,
                'continues-after': segment.continuesAfter,
              },
            ]"
            :style="segmentStyle(segment)"
            @click="emit('eventClick', segment.event, $event)"
            @contextmenu="emit('eventContextmenu', segment.event, $event)"
            @dblclick.stop="emit('eventDblclick', segment.event, $event)"
            @dragend="onEventDragEnd"
            @dragstart="onEventDragStart($event, segment.event)"
          >
            <slot :all-day="false" :event="segment.event" name="event">
              <NxEventChip
                v-bind="chipProps"
                :event="segment.event"
                :past="isPast(segment.event)"
                :resizable="resizableEvents && !segment.continuesAfter"
                :selected="selectedEventId !== null && selectedEventId === segment.event.id"
                :size="chipSize(segment)"
              />
            </slot>
          </div>
          <div
            v-if="nowLine && nowLine.index === dayIndex"
            class="nx-time-grid__now"
            aria-hidden="true"
            :style="{ top: nowLine.top }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nx-time-grid {
  --tg-hour-height: calc(var(--nxt-size-unit) * 3);
  --tg-template: calc(var(--nxt-size-unit) * 3.5) repeat(var(--tg-columns), minmax(0, 1fr));
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
}

/* One scroller for head and body, so a scrollbar narrows both alike and the
   columns stay aligned; the head sticks to its top. */
/* Desktop: a wider gutter for the clock pill and „KW 41“. */
.nx-time-grid.is-desktop {
  --tg-template: calc(var(--nxt-size-unit) * 4) repeat(var(--tg-columns), minmax(0, 1fr));
}

.nx-time-grid__scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.nx-time-grid__row {
  display: grid;
  grid-template-columns: var(--tg-template);
}

.nx-time-grid__head {
  position: sticky;
  top: 0;
  z-index: var(--nxt-z-sticky);
  background: var(--nxt-surface-bg);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border);
}

/* The corner: „KW 41“ as a quiet pill, centred over the hour gutter. */
.nx-time-grid__corner {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-width: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-2xs);
}

.nx-time-grid__week {
  padding: var(--nxt-hairline) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.nx-time-grid__day-head {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-2xs);
  border-inline-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
  font-size: var(--nxt-font-size-sm);
}

.nx-time-grid__weekday {
  color: var(--nxt-fg-muted);
}

.nx-time-grid__daynum {
  display: inline-grid;
  place-items: center;
  min-width: var(--nxt-icon-size-lg);
  height: var(--nxt-icon-size-lg);
  border-radius: var(--nxt-radius-full);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
}

.nx-time-grid__day-head.is-today .nx-time-grid__daynum {
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
}

/* Desktop: the weekday in small capitals over a large day number. */
.nx-time-grid.is-desktop .nx-time-grid__day-head {
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding-block: calc(var(--nxt-space-xs) + var(--nxt-space-2xs));
  line-height: var(--nxt-line-height-tight);
}

.nx-time-grid.is-desktop .nx-time-grid__weekday {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  text-transform: uppercase;
}

.nx-time-grid.is-desktop .nx-time-grid__daynum {
  min-width: var(--nxt-control-size-md);
  height: var(--nxt-control-size-md);
  font-size: var(--nxt-font-size-xl);
  font-weight: var(--nxt-font-weight-regular);
}

.nx-time-grid.is-desktop .nx-time-grid__day-head.is-today .nx-time-grid__weekday {
  color: var(--nxt-link);
}

/* `dayCaption`: a quiet line under the day number. */
.nx-time-grid__day-caption {
  max-width: 100%;
  overflow: hidden;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Classic: weekday and number share a row, so the caption wraps to a line of
   its own under it instead of squeezing in beside them. */
.nx-time-grid:not(.is-desktop) .nx-time-grid__day-head:has(> .nx-time-grid__day-caption) {
  flex-wrap: wrap;
}

.nx-time-grid:not(.is-desktop) .nx-time-grid__day-caption {
  flex-basis: 100%;
  text-align: center;
}

.nx-time-grid__lane {
  grid-auto-rows: auto;
  border-block-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
}

.nx-time-grid__lane-label {
  grid-column: 1;
  align-self: center;
  padding: var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-align: end;
}

/* Desktop: label and „Weniger“ stacked at the end of the gutter, in a step
   smaller type. */
.nx-time-grid.is-desktop .nx-time-grid__lane-label {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: var(--nxt-space-2xs);
  align-self: stretch;
  min-width: 0;
  font-size: calc(var(--nxt-size-unit) * 0.6875);
}

/* The label stays inside the hour gutter: it shrinks to an ellipsis rather
   than running into the first day. */
.nx-time-grid.is-desktop .nx-time-grid__lane-label-text {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nx-time-grid__lane-more,
.nx-time-grid__lane-toggle {
  position: relative;
  z-index: 1;
  justify-self: start;
  max-width: 100%;
  margin-inline: var(--nxt-space-2xs);
  padding: 0 var(--nxt-space-2xs);
  overflow: hidden;
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-muted);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.nx-time-grid__lane-toggle {
  margin-inline: 0;
}

.nx-time-grid__lane-more:hover,
.nx-time-grid__lane-toggle:hover {
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg);
}

.nx-time-grid__lane-more:focus-visible,
.nx-time-grid__lane-toggle:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
}

.nx-time-grid__lane-cell {
  min-height: var(--nxt-control-size-sm);
  border-inline-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
}

.nx-time-grid__lane-event {
  position: relative;
  z-index: 1;
  min-width: 0;
  padding: var(--nxt-hairline) var(--nxt-space-2xs);
}

.nx-time-grid__gutter {
  position: relative;
  display: flex;
  flex-direction: column;
}

/* The clock at the now line, as a pill in the gutter. */
.nx-time-grid__now-time {
  position: absolute;
  inset-inline-end: var(--nxt-space-2xs);
  z-index: 2;
  padding: 0 var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-danger-bg);
  color: var(--nxt-danger-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  font-variant-numeric: tabular-nums;
  line-height: var(--nxt-line-height-normal);
  transform: translateY(-50%);
}

.nx-time-grid__hour {
  box-sizing: border-box;
  height: var(--tg-hour-height);
  padding-inline-end: var(--nxt-space-xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  text-align: end;
}

/* The label sits on its hour line, not under it. */
.nx-time-grid__hour span {
  position: relative;
  top: calc(var(--nxt-font-size-xs) * -0.6);
}

.nx-time-grid__hour:first-child span {
  top: 0;
}

.nx-time-grid__column {
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(var(--tg-hour-height) * var(--tg-hours));
  min-width: 0;
  border-inline-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
}

.nx-time-grid__column.is-today {
  background: color-mix(in srgb, var(--nxt-brand-bg) 4%, transparent);
}

.nx-time-grid__slot {
  flex: 1;
  min-height: 0;
  border-block-start: var(--nxt-hairline) dotted var(--nxt-border-subtle);
  outline: none;
}

.nx-time-grid__slot.is-hour {
  border-block-start-style: solid;
}

/* Desktop: hour lines, half hours a finer hairline, finer slots none — never dots. */
.nx-time-grid.is-desktop .nx-time-grid__slot {
  border-block-start: var(--nxt-hairline) solid transparent;
}

.nx-time-grid.is-desktop .nx-time-grid__slot.is-hour {
  border-block-start-color: var(--nxt-border-subtle);
}

.nx-time-grid.is-desktop .nx-time-grid__slot.is-half {
  border-block-start-color: color-mix(in srgb, var(--nxt-border-subtle) 45%, transparent);
}

.nx-time-grid__slot:hover {
  background: var(--nxt-surface-sunken-bg);
}

.nx-time-grid__slot:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}

.nx-time-grid__slot.is-drop-target,
.nx-time-grid__lane-cell.is-drop-target {
  background: color-mix(in srgb, var(--nxt-brand-bg) 14%, transparent);
}

.nx-time-grid__event {
  position: absolute;
  z-index: 1;
  box-sizing: border-box;
  min-height: var(--nxt-control-size-xs);
  padding: var(--nxt-hairline) var(--nxt-hairline) var(--nxt-hairline) 0;
}

.nx-time-grid__event.continues-before :deep(.nx-event-chip) {
  border-start-start-radius: 0;
  border-start-end-radius: 0;
}

.nx-time-grid__event.continues-after :deep(.nx-event-chip) {
  border-end-start-radius: 0;
  border-end-end-radius: 0;
}

.nx-time-grid__lane-event.continues-before :deep(.nx-event-chip) {
  border-start-start-radius: 0;
  border-end-start-radius: 0;
}

.nx-time-grid__lane-event.continues-after :deep(.nx-event-chip) {
  border-start-end-radius: 0;
  border-end-end-radius: 0;
}

/* While a block is dragged the other blocks let the drop through to the slot
   underneath — otherwise a drop onto a busy hour would land on a block. */
.nx-time-grid.is-dragging .nx-time-grid__event,
.nx-time-grid.is-dragging .nx-time-grid__lane-event {
  pointer-events: none;
}

.nx-time-grid__now {
  position: absolute;
  inset-inline: 0;
  z-index: 2;
  height: calc(var(--nxt-hairline) * 2);
  background: var(--nxt-danger-bg);
  pointer-events: none;
}

.nx-time-grid__now::before {
  content: '';
  position: absolute;
  inset-inline-start: calc(var(--nxt-space-2xs) * -1);
  top: calc(var(--nxt-space-2xs) * -0.75);
  width: var(--nxt-space-xs);
  height: var(--nxt-space-xs);
  border-radius: 50%;
  background: var(--nxt-danger-bg);
}

@media (forced-colors: active) {
  .nx-time-grid__now,
  .nx-time-grid__now::before,
  .nx-time-grid__now-time {
    forced-color-adjust: none;
    background: highlight;
    color: highlighttext;
  }
}
</style>
